import type { PrismaClient } from '@prisma/client';
import Decimal from 'decimal.js';
import { Router } from 'express';
import rateLimit from 'express-rate-limit';
import { z } from 'zod';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, parse } from '../../core/http.js';
import { outstanding } from './calc.js';
import {
  applyOptionalSelection,
  docInclude,
  isoOf,
  isOverdue,
  logEvent,
} from './documents.service.js';
import { renderDocumentPdf } from './pdf.service.js';
import { cancelDocumentReminders } from './reminders.service.js';
import { resolveShareLink } from './share.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

/**
 * Public, unauthenticated, read-only client page (PLAN.md section 5). The only way to reach a document
 * is its 128-bit token; nothing here exposes internal ids. The only write actions are the explicit
 * "accept" and "decline" of a quotation or proforma.
 */
export function createPublicRouter({ db, config }: Deps) {
  const router = Router();
  router.use(
    '/public',
    rateLimit({
      windowMs: 60_000,
      limit: config.NODE_ENV === 'test' ? 100_000 : 60,
      standardHeaders: 'draft-7',
      legacyHeaders: false,
      message: { error: { code: 'RATE_LIMITED', message: 'Too many requests' } },
    }),
  );

  const notFound = () => new HttpError(404, 'NOT_FOUND', 'This link is not valid or has expired');

  async function load(token: string) {
    const link = await resolveShareLink(db, token);
    if (!link) throw notFound();
    return link;
  }

  router.get(
    '/public/documents/:token',
    asyncHandler(async (req, res) => {
      const link = await load(String(req.params.token));
      const company = await db.company.findUniqueOrThrow({ where: { id: link.companyId } });
      const companyView = {
        name: company.name,
        tin: company.tin,
        vatNumber: company.vatNumber,
        vatRegistered: company.vatRegistered,
        address: company.address,
        phone: company.phone,
        email: company.email,
        website: company.website,
        brandColor: company.brandColor,
      };
      await db.shareLink.update({
        where: { id: link.id },
        data: { lastViewedAt: new Date(), viewCount: { increment: 1 } },
      });

      if (link.entityType === 'receipt') {
        const r = await db.paymentReceipt.findFirst({
          where: { id: link.entityId, companyId: link.companyId },
          include: { allocations: true },
        });
        if (!r) throw notFound();
        const party = await db.party.findUnique({
          where: { id: r.partyId },
          select: { name: true },
        });
        const invoices = await db.salesDocument.findMany({
          where: { id: { in: r.allocations.map((a) => a.invoiceId) } },
          select: { id: true, number: true },
        });
        res.json({
          kind: 'receipt',
          company: companyView,
          number: r.receiptNo,
          status: r.status,
          clientName: party?.name,
          currency: r.currency,
          date: isoOf(r.receiptDate),
          amount: r.amount.toFixed(4),
          whtWithheld: r.whtWithheld.toFixed(4),
          reference: r.reference,
          lines: r.allocations.map((a) => ({
            description: `Payment for invoice ${invoices.find((i) => i.id === a.invoiceId)?.number ?? ''}`,
            amount: a.amount.toFixed(4),
          })),
          actions: [],
        });
        return;
      }

      if (link.entityType === 'purchase_order') {
        const o = await db.purchaseOrder.findFirst({
          where: { id: link.entityId, companyId: link.companyId },
          include: { lines: { orderBy: { lineNo: 'asc' } } },
        });
        if (!o || ['draft', 'pending_approval'].includes(o.status)) throw notFound();
        const vendor = await db.party.findUnique({
          where: { id: o.partyId },
          select: { name: true, tin: true, address: true, city: true },
        });
        res.json({
          kind: 'purchaseorder',
          company: companyView,
          number: o.number,
          status: o.status,
          clientName: vendor?.name,
          clientTin: vendor?.tin,
          clientAddress: [vendor?.address, vendor?.city].filter(Boolean).join(', '),
          currency: o.currency,
          issueDate: isoOf(o.issueDate),
          validUntil: null,
          dueDate: isoOf(o.expectedDate),
          reference: o.reference,
          notes: o.notes,
          terms: o.terms,
          subtotal: o.subtotal.toFixed(4),
          taxTotal: o.taxTotal.toFixed(4),
          total: o.total.toFixed(4),
          lines: o.lines.map((l) => ({
            id: l.id,
            kind: 'item',
            description: l.description,
            quantity: l.quantity.toString(),
            unit: l.unit,
            unitPrice: l.unitPrice.toString(),
            discountType: l.discountType,
            discountValue: l.discountValue.toString(),
            vatRate: l.vatRate.toString(),
            lineNet: l.lineNet.toFixed(4),
            isOptional: false,
            isSelected: true,
          })),
          actions: [],
        });
        return;
      }
      if (link.entityType === 'payout') {
        const p = await db.payoutReceipt.findFirst({
          where: { id: link.entityId, companyId: link.companyId, status: 'posted' },
          include: { allocations: true },
        });
        if (!p) throw notFound();
        const vendor = await db.party.findUnique({
          where: { id: p.partyId },
          select: { name: true },
        });
        const bills = await db.vendorBill.findMany({
          where: {
            id: { in: p.allocations.filter((a) => a.targetType === 'bill').map((a) => a.targetId) },
          },
          select: { id: true, number: true },
        });
        res.json({
          kind: 'payout',
          company: companyView,
          number: p.payoutNo,
          status: p.status,
          clientName: vendor?.name,
          currency: p.currency,
          date: isoOf(p.payoutDate),
          amount: p.amount.toFixed(4),
          whtWithheld: p.whtWithheld.toFixed(4),
          reference: p.reference,
          lines: p.allocations.map((a) => ({
            description:
              a.targetType === 'bill'
                ? `Payment of bill ${bills.find((b) => b.id === a.targetId)?.number ?? ''}`
                : 'Reimbursement',
            amount: a.amount.toFixed(4),
          })),
          actions: [],
        });
        return;
      }
      const doc = await db.salesDocument.findFirst({
        where: { id: link.entityId, companyId: link.companyId },
        include: docInclude,
      });
      if (!doc || ['draft', 'pending_approval'].includes(doc.status)) throw notFound();
      const party = await db.party.findUnique({
        where: { id: doc.partyId },
        select: { name: true, tin: true, address: true, city: true },
      });
      // First view: record it (no tracking pixel; only opening the secure link counts).
      if (!doc.viewedAt) {
        await db.salesDocument.update({
          where: { id: doc.id },
          data: {
            viewedAt: new Date(),
            ...(doc.docType === 'quotation' && doc.status === 'sent' ? { status: 'viewed' } : {}),
          },
        });
        await logEvent(db, {
          companyId: doc.companyId,
          entityType: 'sales_document',
          entityId: doc.id,
          type: 'viewed',
        });
      }
      const canRespond =
        ['quotation', 'proforma'].includes(doc.docType) &&
        ['approved', 'sent', 'viewed'].includes(doc.status) &&
        (!doc.validUntil ||
          doc.validUntil >= new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z'));
      res.json({
        kind: doc.docType,
        company: companyView,
        number: doc.number,
        status: isOverdue(doc) ? 'overdue' : doc.status,
        clientName: party?.name,
        clientTin: party?.tin,
        clientAddress: [party?.address, party?.city].filter(Boolean).join(', '),
        currency: doc.currency,
        issueDate: isoOf(doc.issueDate),
        dueDate: isoOf(doc.dueDate),
        validUntil: isoOf(doc.validUntil),
        reference: doc.reference,
        notes: doc.notes,
        terms: doc.terms,
        subtotal: doc.subtotal.toFixed(4),
        taxTotal: doc.taxTotal.toFixed(4),
        total: doc.total.toFixed(4),
        amountPaid: doc.amountPaid.toFixed(4),
        outstanding: outstanding(doc).toFixed(4),
        paymentInstructions: ['invoice', 'proforma'].includes(doc.docType)
          ? company.paymentInstructions
          : null,
        acceptedByName: doc.acceptedByName,
        acceptedAt: doc.acceptedAt,
        declinedAt: doc.declinedAt,
        lines: doc.lines.map((l) => ({
          id: l.id,
          kind: l.kind,
          description: l.description,
          quantity: l.quantity.toString(),
          unit: l.unit,
          unitPrice: l.unitPrice.toString(),
          discountType: l.discountType,
          discountValue: l.discountValue.toString(),
          vatRate: l.vatRate.toString(),
          lineNet: l.lineNet.toFixed(4),
          isOptional: l.isOptional,
          isSelected: l.isSelected,
        })),
        actions: canRespond ? ['accept', 'decline'] : [],
      });
    }),
  );

  router.get(
    '/public/documents/:token/pdf',
    asyncHandler(async (req, res) => {
      const link = await load(String(req.params.token));
      const kind =
        link.entityType === 'receipt'
          ? 'receipt'
          : link.entityType === 'purchase_order'
            ? 'purchaseorder'
            : link.entityType === 'payout'
              ? 'payout'
              : ((await db.salesDocument.findFirst({
                  where: { id: link.entityId, companyId: link.companyId },
                  select: { docType: true, status: true },
                })) ??
                (() => {
                  throw notFound();
                })());
      if (typeof kind !== 'string' && ['draft', 'pending_approval'].includes(kind.status))
        throw notFound();
      const { pdf, filename } = await renderDocumentPdf(
        db,
        link.companyId,
        typeof kind === 'string' ? kind : (kind.docType as never),
        link.entityId,
      );
      res.setHeader('Content-Type', 'application/pdf');
      res.setHeader('X-Content-Type-Options', 'nosniff');
      res.setHeader('Content-Disposition', `attachment; filename="${filename.replace(/"/g, '')}"`);
      res.send(pdf);
    }),
  );

  async function respondable(token: string) {
    const link = await load(token);
    if (link.entityType !== 'sales_document') throw notFound();
    const doc = await db.salesDocument.findFirst({
      where: { id: link.entityId, companyId: link.companyId },
      include: docInclude,
    });
    if (!doc || !['quotation', 'proforma'].includes(doc.docType)) throw notFound();
    if (!['approved', 'sent', 'viewed'].includes(doc.status))
      throw new HttpError(
        409,
        'ALREADY_RESPONDED',
        doc.status === 'accepted'
          ? 'This has already been accepted'
          : doc.status === 'declined'
            ? 'This has already been declined'
            : 'This can no longer be answered online',
      );
    if (
      doc.validUntil &&
      doc.validUntil < new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z')
    )
      throw new HttpError(
        409,
        'EXPIRED',
        'This quotation has expired. Please ask for an updated one.',
      );
    return doc;
  }

  const stamp = (req: { ip?: string; header: (n: string) => string | undefined }) => ({
    ip: req.ip ?? null,
    ua: (req.header('user-agent') ?? '').slice(0, 120),
  });

  router.post(
    '/public/documents/:token/accept',
    asyncHandler(async (req, res) => {
      const body = parse(
        z.object({
          name: z.string().trim().min(2, 'Please type your full name').max(200),
          selection: z.record(z.boolean()).optional(),
        }),
        req.body,
      );
      const doc = await respondable(String(req.params.token));
      const now = new Date();
      await db.$transaction(async (tx) => {
        if (body.selection) {
          const selection: Record<number, boolean> = {};
          for (const [k, v] of Object.entries(body.selection))
            if (doc.lines.some((l) => l.id === Number(k) && l.isOptional)) selection[Number(k)] = v;
          await applyOptionalSelection(tx, doc, selection);
        }
        await tx.salesDocument.update({
          where: { id: doc.id },
          data: {
            status: 'accepted',
            acceptedAt: now,
            acceptedByName: body.name,
            respondedAt: now,
          },
        });
        await logEvent(tx, {
          companyId: doc.companyId,
          entityType: 'sales_document',
          entityId: doc.id,
          type: 'client_accepted',
          note: body.name,
          meta: stamp(req),
        });
        if (doc.ownerId) {
          await tx.notification.create({
            data: {
              companyId: doc.companyId,
              userId: doc.ownerId,
              title: `${doc.number} was accepted by ${body.name}`,
              link: `/sales/${doc.docType === 'quotation' ? 'quotations' : 'proformas'}/${doc.id}`,
            },
          });
        }
      });
      await cancelDocumentReminders(db, doc.docType, doc.id);
      res.json({ ok: true, acceptedAt: now });
    }),
  );

  router.post(
    '/public/documents/:token/decline',
    asyncHandler(async (req, res) => {
      const body = parse(
        z.object({
          name: z.string().trim().min(2).max(200),
          reason: z.string().trim().max(500).optional(),
        }),
        req.body,
      );
      const doc = await respondable(String(req.params.token));
      const now = new Date();
      await db.$transaction(async (tx) => {
        await tx.salesDocument.update({
          where: { id: doc.id },
          data: {
            status: 'declined',
            declinedAt: now,
            declineReason: body.reason ?? null,
            respondedAt: now,
          },
        });
        await logEvent(tx, {
          companyId: doc.companyId,
          entityType: 'sales_document',
          entityId: doc.id,
          type: 'client_declined',
          note: body.reason ? `${body.name}: ${body.reason}` : body.name,
          meta: stamp(req),
        });
        if (doc.ownerId)
          await tx.notification.create({
            data: {
              companyId: doc.companyId,
              userId: doc.ownerId,
              title: `${doc.number} was declined by ${body.name}`,
              body: body.reason,
              link: `/sales/${doc.docType === 'quotation' ? 'quotations' : 'proformas'}/${doc.id}`,
            },
          });
      });
      await cancelDocumentReminders(db, doc.docType, doc.id);
      res.json({ ok: true, declinedAt: now });
    }),
  );

  void Decimal;
  return router;
}
